import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import AppContext from '../contexts/AppContext';
import { resolveSelectedTargetLanguageId } from '../utils/languageUtils';

const sameLanguageIds = (a = [], b = []) =>
  a.length === b.length && a.every((lang, index) => lang?.id === b[index]?.id);

const getInitialState = (user) => {
  return {
    // Step 1: Details
    presence: 'remote', // remote, onsite, announcement
    mode: user?.callType || 'call', // data (phone), video, conference
    selectedLanguageId: user?.selectedLanguageId || null,
    selectedTargetLanguageId: user?.selectedTargetLanguageId || null,
    selectedContractId: null,
    selectedContractName: '',
    timeSlots: [
      {
        date: null,
        startTime: null,
        duration: null, // in minutes
        endTime: null, // calculated
      },
    ],

    // Step 2: Preferences
    gender: null, // null (does not matter), 'female', 'male'
    competenceLevel: [], // array of skill IDs: authorized, court/legal, healthcare, registered, other
    selectedDialect: null, // dialect skill ID
    requirements: null, // null (no requirements), 'security-cleared'
    additionalPreferences: '',
    additionalPreferencesExpanded: false,

    // Soft limit preferences
    acceptAlternativeLanguage: undefined, // Accept alternative language if primary not available
    alternativeLanguageId: null, // Which language to use as alternative
    backupCallTypeForOnSite: null, // 'call' (phone) or 'data' (video) when using remote as backup for onsite

    // Step 3: Invoicing & Assignment Details
    // Only default a VAT status when the customer is actually shown the toggle
    // (vatPolicy === 'select'). Otherwise leave it undefined so the backend applies
    // the usergroup's own policy instead of the client inventing one (TUL-5776).
    vatIncluded: user?.userGroup?.vatPolicy === 'select' ? true : undefined,
    costAllocation: user?.invoiceUserGroup?.id || null, // User group ID or 'other'
    additionalInvoicingInfo: '',
    customerName: '', // For announcement variant only
    dateOfBirth: null, // For announcement variant only
    ccEmails: [],
    additionalInfoForHandler: '',
    additionalInfoExpanded: false,

    // Legacy fields (keeping for backwards compatibility)
    invoiceUserGroupId: user?.invoiceUserGroup?.id || null,
    clientName: '',
    subject: '',
    externalOrderId: '',

    // Attachment for prebooking - uses presigned S3 URLs (TUL-3848)
    attachment: null, // { file: File, filename: string, contentType: string, size: number }

    // Customer info (from user or form)
    customerNumber: user?.customerNumber || '',
    orderMadeBy: user?.username || '',
    orderingUnit: '',

    // Language and skill data from user context (matching ChatWizard pattern)
    selectableLanguages: user?.selectableLanguages || [],
    targetLanguages: user?.targetLanguages || [],
    selectableSkills: user?.selectableSkills || {},
    languagePairs: user?.languagePairs || new Map(),

    // Location fields with userGroup prefill
    streetAddress: user?.userGroup?.streetAddress || '',
    postCode: user?.userGroup?.postcode || '',
    city: user?.userGroup?.locationCity || '',
    arrivalInstruction: user?.userGroup?.locationInfo || '',
  };
};

const usePrebookingState = () => {
  const { user } = useContext(AppContext);
  const [formData, setFormData] = useState(getInitialState(user));
  const originalEditDataRef = useRef(null);
  // True once the VAT status came from the booking being edited or from the customer's own
  // choice — from then on the policy default must not overwrite it (TUL-5776).
  const vatChosenRef = useRef(false);
  const vatPolicy = user?.userGroup?.vatPolicy;

  // AppContext seeds `user` from localStorage and then refreshes it from the API, on mount and
  // every 5 minutes (containers/app.js `fetchUserAndUpdateLocally`), so the policy behind the
  // initial default can change under an open form. Re-derive the default when it does: a stale
  // cache would otherwise leave a `select` group with no VAT status to send, or keep sending a
  // hidden `true` after the policy turned out not to let the customer choose (TUL-5776).
  useEffect(() => {
    if (vatChosenRef.current) return;
    const policyDefault = vatPolicy === 'select' ? true : undefined;
    setFormData((prev) => (
      prev.vatIncluded === policyDefault ? prev : { ...prev, vatIncluded: policyDefault }
    ));
  }, [vatPolicy]);

  const updateField = useCallback((field, value) => {
    if (field === 'vatIncluded') {
      vatChosenRef.current = true;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateFields = useCallback((updates) => {
    if (updates && 'vatIncluded' in updates) {
      vatChosenRef.current = true;
    }
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // TUL-4996: narrow the bookable target (customer) languages to those the selected contract
  // allows. `fullTargetLanguages` is the unfiltered list from the user context; `allowedLanguageIds`
  // is the selected contract's targetLanguageIds (empty/nullish => no restriction). The selected
  // target language is reset when it falls outside the narrowed list. Returns `prev` untouched when
  // nothing changes so the effect that calls this cannot loop.
  const applyContractTargetLanguages = useCallback((fullTargetLanguages = [], allowedLanguageIds) => {
    const filtered = (allowedLanguageIds && allowedLanguageIds.length > 0)
      ? fullTargetLanguages.filter((lang) => allowedLanguageIds.includes(lang.languageId))
      : fullTargetLanguages;

    setFormData((prev) => {
      const nextSelectedTargetLanguageId = resolveSelectedTargetLanguageId(filtered, prev.selectedTargetLanguageId);

      if (
        sameLanguageIds(prev.targetLanguages, filtered) &&
        prev.selectedTargetLanguageId === nextSelectedTargetLanguageId
      ) {
        return prev;
      }

      return {
        ...prev,
        targetLanguages: filtered,
        selectedTargetLanguageId: nextSelectedTargetLanguageId,
      };
    });
  }, []);

  const addTimeSlot = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      timeSlots: [
        ...prev.timeSlots,
        {
          date: null,
          startTime: null,
          duration: null,
          endTime: null,
        },
      ],
    }));
  }, []);

  const removeTimeSlot = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index),
    }));
  }, []);

  const updateTimeSlot = useCallback((index, field, value) => {
    setFormData((prev) => {
      const newTimeSlots = [...prev.timeSlots];
      newTimeSlots[index] = {
        ...newTimeSlots[index],
        [field]: value,
      };

      // Auto-calculate end time if date, startTime, and duration are set
      if (field === 'duration' || field === 'startTime') {
        const slot = newTimeSlots[index];
        if (slot.startTime && slot.duration) {
          const endTime = new Date(slot.startTime.getTime() + slot.duration * 60000);
          newTimeSlots[index].endTime = endTime;
        }
      }

      return {
        ...prev,
        timeSlots: newTimeSlots,
      };
    });
  }, []);

  const addEmail = useCallback((email) => {
    setFormData((prev) => ({
      ...prev,
      ccEmails: [...prev.ccEmails, email],
    }));
  }, []);

  const removeEmail = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((_, i) => i !== index),
    }));
  }, []);

  const addPhone = useCallback((phone) => {
    setFormData((prev) => ({
      ...prev,
      additionalPhoneNumbers: [...(prev.additionalPhoneNumbers || []), phone],
    }));
  }, []);

  const removePhone = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      additionalPhoneNumbers: (prev.additionalPhoneNumbers || []).filter((_, i) => i !== index),
    }));
  }, []);

  const updateAttachment = useCallback((file) => {
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        attachment: null,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      attachment: {
        file: file,
        filename: file.name,
        contentType: file.type,
        size: file.size,
      },
    }));
  }, []);

  const clearAttachment = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      attachment: null,
    }));
  }, []);

  const initializeFromPrebooking = useCallback((transformedData) => {
    if (transformedData.vatIncluded !== undefined) {
      vatChosenRef.current = true;
    }
    setFormData((prev) => {
      // Reverse-lookup targetLanguage entry ID from the actual languageId
      if (transformedData.selectedTargetLanguageId) {
        const targetLangEntry = prev.targetLanguages?.find(
          l => l.languageId === transformedData.selectedTargetLanguageId
        );
        if (targetLangEntry) {
          transformedData.selectedTargetLanguageId = targetLangEntry.id;
        }
      }

      const mergedData = {
        ...prev,
        // Preserve user context fields
        selectableLanguages: prev.selectableLanguages,
        targetLanguages: prev.targetLanguages,
        selectableSkills: prev.selectableSkills,
        languagePairs: prev.languagePairs,
        customerNumber: prev.customerNumber,
        orderMadeBy: prev.orderMadeBy,
        // Apply transformed prebooking data
        ...transformedData,
      };

      // Capture snapshot for edit diff (after mutation is applied)
      originalEditDataRef.current = { ...mergedData };

      return mergedData;
    });
  }, []);

  const resetForm = useCallback(() => {
    vatChosenRef.current = false;
    setFormData(getInitialState(user));
  }, [user]);

  return {
    formData,
    updateField,
    updateFields,
    applyContractTargetLanguages,
    addTimeSlot,
    removeTimeSlot,
    updateTimeSlot,
    addEmail,
    removeEmail,
    addPhone,
    removePhone,
    updateAttachment,
    clearAttachment,
    initializeFromPrebooking,
    resetForm,
    originalEditData: originalEditDataRef,
  };
};

export default usePrebookingState;
